import React from "react";
import { Lead, Section, P, CTACard } from "../../../../blog/components";
import ctaBg from "../../../../assets/demo/screen-01.png";

export const title = "vibecoding 上瘾";
export const excerpt =
  "vibe coding 跑的是和老虎机一样的奖励节奏：无法预测的兑现、感觉像在进展的擦肩而过，以及「再来一个 prompt」的拉力。研究正开始跟上这一点。";
export const heroAlt =
  "莫比乌斯风格的风景：一条发光蜿蜒的光之小径穿过广阔草原，通向落日地平线，几个小小的身影从开满花的沙丘上、在柔和的云彩下望着";
export const readingTime = "6 分钟阅读";

const BLUE = "#5ba8f5";

/** In-text footnote marker: a real link to the cited source (opens in a new tab). */
function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BLUE, textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

/* ── References ───────────────────────────────────────────────────────────── */

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "M. Fawzy, A. Tahir, K. Blincoe，《Vibe Coding in Practice: Motivations, Challenges, and a Future Outlook: A Grey Literature Review》（2025）。用于「64% 的实践者报告了『瞬间成功与心流』，被描述为神奇、多巴胺冲击、令人上瘾的势头」这一发现，以及由此产生的对代码质量的错误自信。",
    href: "https://arxiv.org/abs/2510.00328",
    label: "arxiv.org/abs/2510.00328",
  },
  {
    text: "R. Thomas，《Breaking the Spell of Vibe Coding》，fast.ai（2026 年 1 月）。用于「暗心流／垃圾心流」的框架、把损失伪装成胜利的机制，以及与 Csikszentmihalyi 那种能构建技能的心流之间的对比。",
    href: "https://www.fast.ai/posts/2026-01-28-dark-flow/",
    label: "fast.ai/posts/2026-01-28-dark-flow",
  },
  {
    text: "L. Clark, A. J. Lawrence, F. Astley-Jones, N. Gray，《Gambling Near-Misses Enhance Motivation to Gamble and Recruit Win-Related Brain Circuitry》，Neuron（2009）。用于「擦肩而过会调动与胜利相关的脑回路，并在没有任何金钱奖励的情况下增强继续玩下去的冲动」这一发现。",
    href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC2658737/",
    label: "ncbi.nlm.nih.gov/…/Clark-2009-near-misses",
  },
  {
    text: "H. W. Chase, L. Clark，《Gambling Severity Predicts Midbrain Response to Near-Miss Outcomes》，Journal of Neuroscience（2010）。用于「中脑对擦肩而过的多巴胺反应强度与赌博严重程度相关」这一发现。",
    href: "https://www.jneurosci.org/content/30/18/6180",
    label: "jneurosci.org/content/30/18/6180",
  },
  {
    text: "METR，《Measuring the Impact of Early-2025 AI on Experienced Open-Source Developer Productivity》（2025）。用于那项随机试验：开发者预期约提速 24%，感知到约提速 20%，实测使用 AI 工具后慢了 19%。",
    href: "https://arxiv.org/abs/2507.09089",
    label: "arxiv.org/abs/2507.09089",
  },
  {
    text: "A. Filev，《One More Prompt: How Vibe Coding's Casino Mechanics Cost Me a Billion Dollars》，Unite.AI（2025 年 10 月）。用于可变奖励与擦肩而过的框架、「再来一个 prompt」的循环，以及「这种拉力对刚开始做软件的人最强」这一观点。",
    href: "https://www.unite.ai/one-more-prompt-how-vibe-codings-casino-mechanics-cost-me-a-billion-dollars/",
    label: "unite.ai/…/one-more-prompt-casino-mechanics",
  },
  {
    text: "《Professional Software Developers Don't Vibe, They Control: AI Agent Use for Coding in 2025》（2025）。用于「有经验的开发者倾向于操控并拒绝 AI 输出，而不是向循环缴械」这一发现。",
    href: "https://arxiv.org/abs/2512.14012",
    label: "arxiv.org/abs/2512.14012",
  },
];

export default function Post() {
  return (
    <>
      <Lead>
        我想描述一个特定的夜晚，因为我觉得很多人都经历过同样的一晚。你打开编辑器，本来只想修一个小东西。你写了一个 prompt，模型改得挺接近。不算完全对，但很接近。
        于是你又写了一个，又一个。某个时刻你抬起头，三个小时没了，功能还是不好使，而你最强烈的感受，是想再发一个 prompt 的冲动。
      </Lead>

      <P>
        最后那部分才是破绽。不是浪费掉的时间，人做正经好活儿也一样会耗时间。破绽在于那股拉力，那种「想继续下去」的劲头，已经悄悄和「事情到底顺不顺」脱钩了。
      </P>
      <P>
        如今有一小批、且在不断增多的文章，其中一些是学术性的，主张 vibe coding 并不是简单的有生产力或没生产力。它在结构上是令人上瘾的，接近技术意义上的那种上瘾。它跑的，正是让老虎机难以抽身的那套奖励节奏。
      </P>

      <Section title="再来一个 prompt">
        <P>
          经营着一家 AI 编程公司的 Andrew Filev，写下了自己被逮个正着的经历。他当时在度假，家人在海滩上等他，他却一连几个小时对自己说「就再试一次」，而那个 prompt 差一点就成了。<Fn n={6} /> 他不是随便玩玩的用户，他靠造这些工具谋生。可他最终还是用赌场的语言来描述这段体验：无法预测的奖励、擦肩而过，还有那个他始终没能收尾的「再来一个 prompt」循环。
        </P>
        <P>
          他的自述之所以有用，是因为他坦诚地说出了这东西打击谁最狠。对一个有经验的工程师来说，AI 是一种优化，是他们本就会做的事的更快版本。而对一个原本根本做不出软件、如今忽然能做的人来说，这一跃是巨大的，多巴胺也是。<Fn n={6} /> 「我做不到」和「我刚刚做到了」之间的落差越大，那股拉力就越强。
        </P>
      </Section>

      <Section title="它跑的是和老虎机一样的节奏">
        <P>
          行为心理学给我们所知最顽固的那种奖励模式起了个名字：可变比率强化程式。你在经过一个无法预测的尝试次数后得到奖励。不是每次都有，也不是按固定次数，只是频繁到你永远猜不到下一次兑现在哪。鸽子、老鼠和人，在这种程式下都会比在可预测的程式下把一根拉杆按得久得多。这就是老虎机内部的引擎。
        </P>
        <P>
          vibe coding 几乎太贴合这个模式了。有时第一个 prompt 就搞定；有时是第八个才行；有时你白烧掉一个小时。兑现是真实的，是能跑的代码，但它出现的时机是随机的，而随机的时机，恰恰是让拉杆一直动下去的东西。
        </P>
        <P>
          老派的编程兑现得很慢。你可能要隔上几个小时，才迎来「编译通过」或「测试变绿」这样的小胜利。AI 编程把它压缩到了几秒。我反复回看的那篇灰色文献综述发现，被报告得最普遍的单一体验，样本中 64% 的实践者，是「瞬间成功与心流」，常常就用这些原话来形容：神奇、多巴胺冲击、令人上瘾的势头。<Fn n={1} /> 随机节奏上的快速兑现，这正是赌场运转所依据的整套配方。
        </P>
      </Section>

      <Section title="钩子">
        <P>
          在「擦肩而过」这一点上，老虎机的类比变得更加锋利。擦肩而过，就是那一转：两颗樱桃落在线上，第三颗停在线的正上方一点。客观上它就是输，和任何一次输都一样。但它感觉不像输。
        </P>
        <P>
          Luke Clark 和同事们把赌徒放进 fMRI 扫描仪，结果表明擦肩而过会调动许多和真正获胜相同的、与胜利相关的脑回路，而且人们报告说，恰恰在一次擦肩而过之后，继续玩下去的冲动更强，尽管他们什么都没赢到。<Fn n={3} /> 后来一项研究发现，中脑对擦肩而过那种反应的强度，与一个人赌博的严重程度相关。<Fn n={4} /> 对大脑而言，差一点获胜并不是一种失败状态。它是第二种奖励。
        </P>
        <P>
          现在想想编程 Agent 出错时会产出什么。不是垃圾。通常是某种几乎能用的东西。代码编译通过，却挂在一个测试上。页面渲染出来了，但按钮什么都不干。这就是擦肩而过，随叫随到地生成，一小时几十次。每一次都在说「你已经这么接近了，再试一次」，而且和老虎机不同，它往往说的是实话，这让它更难被忽视。
        </P>
      </Section>

      <Section title="心流，还是它的廉价仿制品">
        <P>
          vibe coding 的辩护者会抓住「心流」这个词，这也是一种合理的直觉。那种状态感觉确实像心流：全神贯注、时间飞逝、阻力很低。fast.ai 的 Rachel Thomas 论证说，它其实更接近研究者所称的「暗心流」或「垃圾心流」，就是你从老虎机或无限信息流里得到的那种。<Fn n={2} /> 真正的心流，在 Csikszentmihalyi 最初的意义上，来自一项与你技能相匹配的任务，并在你推进的过程中构建这份技能。垃圾心流则是有全神贯注、却没有成长。从内部体验来看它们一模一样，但你从另一头出来时并没有变得更好，有时反而更差。
        </P>
        <P>
          那篇灰色文献综述在数据里发现了同样的阴暗边缘。瞬间成功的体验，产生了作者们所称的对代码质量的错误自信。<Fn n={1} /> 「觉得自己做了好活儿」和「实际做出了好活儿」这两件事，已经分了家。这才是比浪费掉的时间更该让你担心的部分。
        </P>
      </Section>

      <Section title="它感觉像进展。数字并不同意。">
        <P>
          老虎机研究者有一个词，专指赌场里最残忍的把戏：伪装成胜利的损失。机器返给你的比你下注的少，却照样跑完整段音乐和灯光，于是一次净亏损被登记成了一次赢。<Fn n={2} /> vibe coding 对生产力做了类似的事。它感觉很快。它对自己的进展大声张扬。而实测的结果就没那么好看了。
        </P>
        <P>
          METR 在 2025 年初做了一项随机试验，让有经验的开源开发者在自己的代码库里处理真实任务。事前，开发者预期 AI 会让他们提速约 24%。事后，他们相信 AI 让他们提速了约 20%。而实测，他们使用 AI 工具时比不用时慢了 19%。<Fn n={5} /> 他们感觉更快，实际更慢，感受与实况之间差了将近 40 个百分点。用老虎机的话说，这就是一次伪装成胜利的损失，只不过这一次是在一场有专业工程师参与的对照试验中被测量出来的。
        </P>
      </Section>

      <Section title="它真的算上瘾吗？">
        <P>
          我想在这里谨慎一些，因为很容易说过头。没有一种诊断叫「vibe coding 障碍」。没有人做过那些长期临床研究，那是把这件事称作像赌博障碍那样的上瘾所必需的。我们手上有的，是一种相符的机制，可变比率奖励加上强力的擦肩而过，外加许多人各自独立地伸手去抓同一套赌博词汇来描述自己的行为。这有暗示性，但没有定论。
        </P>
        <P>
          它也不是对每个人都一样地下手。最近一项标题直白得很的研究《Professional Software Developers Don't Vibe, They Control》发现，有经验的工程师往往根本不会向那个循环缴械。他们操控 Agent，拒绝掉它给出的大部分东西，并在自己脑子里保有一份关于系统的模型。<Fn n={7} /> 那股拉力最强的地方，恰恰是安全护栏最薄的地方：刚开始做软件的人，跑得飞快，没有测试，也没有清晰的「什么时候算做完」的判断。
        </P>
      </Section>

      <CTACard download github bgImage={ctaBg} />

      <Section title="参考文献">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
